import {Injectable} from '@angular/core';
import {Subscription} from 'rxjs';
import {MD5} from 'crypto-js';
import {ToastrService} from 'ngx-toastr';
import {TranslateService} from '@ngx-translate/core';

@Injectable({
    providedIn: 'root'
})
export class UtilsService {

    constructor(
        private toastr: ToastrService,
        private translate: TranslateService
    ) {
    }

    openLink(url: string): void {
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }


    safeStableStringify(value: any): string {
        const seen = new WeakSet<object>();

        const replacer = (_key: string, val: any) => {
            if (typeof val === 'function' || typeof val === 'symbol' || val === undefined) return undefined;
            if (val instanceof Subscription) return undefined;
            if (val && typeof val === 'object' && '_parentage' in val) return undefined;
            if (val && typeof val === 'object') {
                if (seen.has(val)) return undefined;
                seen.add(val);
                if (!Array.isArray(val)) {
                    const sorted: any = {};
                    for (const k of Object.keys(val).sort()) sorted[k] = val[k];
                    return sorted;
                }
            }
            return val;
        };

        return JSON.stringify(value, replacer);
    }

    arraysEqualUnordered(a: any[], b: any[]): boolean {
        if (a.length !== b.length) return false;
        const sortedA = [...a].sort();
        const sortedB = [...b].sort();
        return sortedA.every((val, index) => val === sortedB[index]);
    }

    async downloadFile(url: string, filename?: string) {
        let blob: any;
        let name = url.split('/').pop().split('?access_token')[0];
        if (url.startsWith('data:') && url.includes(';base64,')) {
            const arr = url.split(',');
            const mime = arr[0].match(/:(.*?);/)[1];
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);
            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }
            blob = new Blob([u8arr], {type: mime});
            const ext = mime.split('/')[1] || 'bin';
            name = filename || `file.${ext}`;
        } else {
            // cache: 'no-store' forces a fresh network request so we don't reuse a non-CORS
            // response cached by an inline <img>/<pdf-viewer> preview of the same URL. That
            // cached copy lacks Access-Control-Allow-Origin (img/pdf requests send no Origin
            // header), which would make this CORS fetch fail even though the bucket allows
            // our origin. The fresh request sends Origin and gets the proper CORS response.
            const response = await fetch(url, {cache: 'no-store'});
            blob = await response.blob();
            name = filename || name;
        }
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    sortByNameCaseInsensitive<T extends { name?: string }>(a: T, b: T): number {
        const nameA = a.name?.toLowerCase() || '';
        const nameB = b.name?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
    }

    sortByTitleCaseInsensitive<T extends { title?: string }>(a: T, b: T): number {
        const titleA = a.title?.toLowerCase() || '';
        const titleB = b.title?.toLowerCase() || '';
        return titleA.localeCompare(titleB);
    }

    simpleHash(obj: any): string {
        return MD5(JSON.stringify(obj)).toString();
    }

    copyToClipboard(text: string) {
        navigator.clipboard.writeText(text).then(() => {
            this.toastr.success(this.translate.instant('Setup.Integrations.PublicAPI.CopyClipboardSuccess'))
        });
    }

    getQueryParams(): URLSearchParams {
        const hash = window.location.hash;
        const queryString = hash.split('?')[1] ?? '';
        return new URLSearchParams(queryString);
    }

    findCategoryById(categories: any[], id: number): any | null {
        for (const category of categories) {
            if (category.id === id) { return category; }
            if (category.subcategories?.length) {
                const found = this.findCategoryById(category.subcategories, id);
                if (found) { return found; }
            }
        }
        return null;
    }

}
